import React from 'react';
import { View } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { isPhone, isWeb, deepClone, formatTimeEvent, convertTimeLetter } from '@helpers';
import {
  DATA_GRID_IMAGE_COLUMN_WIDTH,
  SCREEN_PADDING_HORIZONTAL,
  TEST_IDS,
} from '../../../v2/pages/common/constants/base.constants';
import { DOCUMENT_ENTITY_TYPE, IMAGE_HANDLING_CONTEXT } from '@components/imageHandling/common';
import { LevelSubScreen } from '@v2/pages/common/enums';
import Image from '@v2/context/Image/context';
import Svg from '@styles/icons/svg';
import { facetName } from '@components/search/components/mapper';
import { DisplayModeFlags } from '@components/search/productionBatches/types';
import { scale } from '@components/frame';

export const LINK_STATUS_ICON: Record<string, string> = {
  NotLinked: 'unknownLink',
  PartialLink: 'notLinked',
  SingleLink: 'linked',
  MultiLink: 'multiLink',
};

export const getIsSquareMode = ({ isInfoTilesMode, isSmallMode, isLineMode, isTableMode }: DisplayModeFlags): boolean => {
  return !isInfoTilesMode && !isSmallMode && !isLineMode && !isTableMode;
};

/**
 * Pads data array with 'empty' entries so it fills complete grid rows
 */
export const padDataForGrid = <T>(data: T[], columns: number): (T | 'empty')[] => {
  const cloned = deepClone(data) as (T | 'empty')[];
  const additions = columns - (cloned.length % columns);
  if (additions !== columns) {
    for (let i = 0; i < additions; i++) {
      cloned.push('empty');
    }
  }
  return cloned;
};

/**
 * Calculates the width for each batch item
 */
export const getItemWidth = ({
  batchListWidth,
  itemSize,
  isLineMode,
  IS_NARROW_WEB,
}: {
  batchListWidth: number;
  itemSize: number;
  isLineMode: boolean;
  IS_NARROW_WEB: boolean;
}): number => {
  if (isPhone && !IS_NARROW_WEB) return batchListWidth - SCREEN_PADDING_HORIZONTAL * 2;
  if (!isWeb && isLineMode) return batchListWidth;
  return itemSize;
};

export const getDataGridColumns = ({
  open,
  attachMode,
}: {
  open: (item: any, levelSubScreen?: any) => void;
  attachMode?: boolean;
}) => [
  {
    field: 'Image',
    headerName: '',
    align: 'center',
    width: scale(DATA_GRID_IMAGE_COLUMN_WIDTH),
    sortable: false,
    filterable: false,
    renderCell: ({ row: item }: { row: any }) => {
      const imgSize = { width: scale(50), height: scale(30) };

      const imageData = {
        image: {
          documentId: item.imageDocumentId,
          imageHash: item.imageHash,
          imageData: {
            isCropped: item.imageData?.isCropped,
            extensionDerivative: item.imageData?.extensionDerivative,
            pageNumber: item.imageData?.pageNumber,
            rotationAngle: item.imageData?.rotationAngle || 0,
            rotationAngleCrop: item.imageData?.rotationAngle || 0,
          },
          width: imgSize.width,
          height: imgSize.height,
          contextId: item.productionBatchId,
          contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.Batch],
        },
        onPress: () => open(item, LevelSubScreen.DOCUMENTS),
        showImageControl: false,
      };

      return React.createElement(
        View,
        {
          testID: TEST_IDS.searchScreen.productionBatches.dataGridImage,
          style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' },
        },
        React.createElement(Image, { ...imageData, attachMode }),
      );
    },
  },
  { field: 'batchNumber', headerName: i18n.t('Batch Number'), align: 'left', width: 150, sortable: true, filterable: true },
  { field: 'ncFileName', headerName: i18n.t('NC File'), align: 'left', flex: 1, sortable: true, filterable: true },
  { field: 'machineFullName', headerName: i18n.t('Machine'), align: 'left', flex: 1, sortable: true, filterable: true },
  {
    field: 'startedOn',
    headerName: i18n.t('Start'),
    align: 'left',
    headerAlign: 'left',
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any) => (value ? moment(value).format('DD/MM/YYYY') : ''),
    flex: 1,
  },
  {
    field: 'endedOn',
    headerName: i18n.t('End'),
    align: 'left',
    headerAlign: 'left',
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any, row: any) => (row.isInProgress ? i18n.t('In Progress') : moment(value).format('DD/MM/YYYY')),
    flex: 1,
  },
  {
    field: 'quantity',
    headerName: i18n.t('Qty'),
    align: 'left',
    headerAlign: 'left',
    sortable: true,
    filterable: true,
  },
  {
    field: 'linksAmount',
    headerName: i18n.t('Qty Linked'),
    align: 'left',
    headerAlign: 'left',
    flex: 1,
    renderCell: ({ row }: { row: any }) => row?.links?.reduce((acc: number, item: any) => acc + (item.quantity || 0), 0) || 0,
    sortable: false,
    filterable: false,
  },
  {
    field: 'linkStatus',
    headerName: i18n.t('Link'),
    flex: 1,
    headerAlign: 'left',
    renderCell: ({ row }: { row: any }) =>
      React.createElement(
        View,
        { style: { width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' } },
        React.createElement(Svg, { icon: LINK_STATUS_ICON[row.linkStatus], width: scale(16), height: scale(16) }),
      ),
    type: 'singleSelect',
    valueOptions: Object.keys(LINK_STATUS_ICON).map((value) => ({ value, label: facetName(value) })),
    sortable: true,
    filterable: true,
  },
  {
    field: 'links',
    headerName: i18n.t('Jobs'),
    minWidth: 200,
    valueGetter: (value: any) => value?.map((l: any) => l.productionOrderNumber)?.join(', '),
    filterable: false,
    sortable: false,
  },
  {
    field: 'durationInSeconds',
    headerName: i18n.t('Elapsed'),
    width: scale(80),
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => formatTimeEvent(convertTimeLetter(value)),
    flex: 1,
  },
  {
    field: 'idealCycleTimeTotalInSeconds',
    headerName: i18n.t('Ideal Cycle Time'),
    align: 'left',
    headerAlign: 'left',
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => formatTimeEvent(convertTimeLetter(value)),
    flex: 1,
  },
  {
    field: 'actualCycleTimeAverageInSeconds',
    headerName: i18n.t('Avg. Cycle Time'),
    align: 'left',
    headerAlign: 'left',
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => formatTimeEvent(convertTimeLetter(value)),
    flex: 1,
  },
  {
    field: 'actualSetupTimeInSeconds',
    headerName: i18n.t('Actual Setup Time'),
    align: 'left',
    headerAlign: 'left',
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => formatTimeEvent(convertTimeLetter(value)),
    flex: 1,
  },
  {
    field: 'idealSetupTimeInSeconds',
    headerName: i18n.t('Ideal Setup Time'),
    align: 'left',
    headerAlign: 'left',
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => formatTimeEvent(convertTimeLetter(value)),
    flex: 1,
  },
  {
    field: 'idealCycleTimeSetManually',
    headerName: i18n.t('Ideal Cycle Time set manually'),
    align: 'left',
    type: 'boolean',
    sortable: true,
    filterable: true,
    flex: 1,
  },
  {
    field: 'idealSetupTimeSetManually',
    headerName: i18n.t('Ideal Setup Time set manually'),
    align: 'left',
    type: 'boolean',
    sortable: true,
    filterable: true,
    flex: 1,
  },
  {
    field: 'isClosed',
    headerName: i18n.t('Is Closed'),
    align: 'left',
    type: 'boolean',
    sortable: true,
    filterable: true,
    flex: 1,
  },
  {
    field: 'isInProgress',
    headerName: i18n.t('In Progress'),
    align: 'left',
    type: 'boolean',
    sortable: true,
    filterable: true,
    flex: 1,
  },
  { field: 'linksSummary', headerName: i18n.t('Links Summary'), align: 'left', flex: 1, sortable: true },
];
